import type { StoreApi } from 'zustand';
import type {
  LayoutState,
  LayoutTabGroupsSlice,
  TabGroup,
} from './layoutStoreTypes';

export const createLayoutTabGroupsSlice = ({
  set,
  get,
}: {
  set: StoreApi<LayoutState>['setState'];
  get: StoreApi<LayoutState>['getState'];
}): LayoutTabGroupsSlice => ({
  // Chat tabs state
  chatTabs: [],
  activeChatTabId: null,

  tabGroups: [],
  tabGroupingSettings: {
    autoGroupFlows: true,
    showGroupColors: true,
    collapseNewGroups: false,
  },

  // Chat tab actions
  setChatTabs: (tabs) => set({ chatTabs: tabs }),
  setActiveChatTabId: (tabId) => set({ activeChatTabId: tabId }),
  addChatTab: (tab) => {
    const { chatTabs } = get();
    set({ chatTabs: [...chatTabs, tab], activeChatTabId: tab.tabId });
  },
  removeChatTab: (tabId) => {
    const { chatTabs, activeChatTabId } = get();
    const filteredTabs = chatTabs.filter((tab) => tab.tabId !== tabId);
    const updates: Partial<LayoutState> = { chatTabs: filteredTabs };

    if (tabId === activeChatTabId && filteredTabs.length > 0) {
      updates.activeChatTabId = filteredTabs[filteredTabs.length - 1].tabId;
    } else if (filteredTabs.length === 0) {
      updates.activeChatTabId = null;
    }

    set(updates);
  },
  updateChatTab: (tabId, updates) => {
    const { chatTabs } = get();
    set({
      chatTabs: chatTabs.map((tab) =>
        tab.tabId === tabId ? { ...tab, ...updates } : tab,
      ),
    });
  },
  clearChatTabs: () => {
    set({ chatTabs: [], activeChatTabId: null });
  },

  // Tab group actions
  createTabGroup: (group) => {
    const id = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const { tabGroups, mainContentTabs } = get();
    const movedTabIds = new Set(group.tabIds);
    const newGroup: TabGroup = {
      ...group,
      id,
    };

    const updatedExistingGroups = tabGroups
      .map((existingGroup) => ({
        ...existingGroup,
        tabIds: existingGroup.tabIds.filter((tabId) => !movedTabIds.has(tabId)),
      }))
      .filter((existingGroup) => existingGroup.tabIds.length > 0);

    const updatedTabs = mainContentTabs.map((tab) =>
      movedTabIds.has(tab.id) ? { ...tab, groupId: id } : tab,
    );

    set({
      tabGroups: [...updatedExistingGroups, newGroup],
      mainContentTabs: updatedTabs,
    });
    return id;
  },

  deleteTabGroup: (groupId) => {
    const { tabGroups, mainContentTabs } = get();
    const updatedTabs = mainContentTabs.map((tab) =>
      tab.groupId === groupId ? { ...tab, groupId: undefined } : tab,
    );
    set({
      tabGroups: tabGroups.filter((group) => group.id !== groupId),
      mainContentTabs: updatedTabs,
    });
  },

  updateTabGroup: (groupId, updates) => {
    const { tabGroups } = get();
    set({
      tabGroups: tabGroups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group,
      ),
    });
  },

  addTabToGroup: (tabId, groupId) => {
    const { tabGroups, mainContentTabs } = get();
    const group = tabGroups.find((g) => g.id === groupId);
    if (!group) return;

    const updatedGroups = tabGroups
      .map((g) => ({
        ...g,
        tabIds:
          g.id === groupId
            ? [...g.tabIds.filter((id) => id !== tabId), tabId]
            : g.tabIds.filter((id) => id !== tabId),
      }))
      .filter((g) => g.tabIds.length > 0);

    const updatedTabs = mainContentTabs.map((tab) =>
      tab.id === tabId ? { ...tab, groupId } : tab,
    );

    set({
      tabGroups: updatedGroups,
      mainContentTabs: updatedTabs,
    });
  },

  removeTabFromGroup: (tabId) => {
    const { tabGroups, mainContentTabs } = get();
    const tab = mainContentTabs.find((t) => t.id === tabId);
    if (!tab?.groupId) return;

    const groupId = tab.groupId;
    const updatedGroups = tabGroups
      .map((group) =>
        group.id === groupId
          ? { ...group, tabIds: group.tabIds.filter((id) => id !== tabId) }
          : group,
      )
      // Remove empty groups
      .filter((group) => group.tabIds.length > 0);

    const updatedTabs = mainContentTabs.map((t) =>
      t.id === tabId ? { ...t, groupId: undefined } : t,
    );

    set({
      tabGroups: updatedGroups,
      mainContentTabs: updatedTabs,
    });
  },

  toggleGroupCollapse: (groupId) => {
    const { tabGroups } = get();
    set({
      tabGroups: tabGroups.map((group) =>
        group.id === groupId
          ? { ...group, isCollapsed: !group.isCollapsed }
          : group,
      ),
    });
  },

  reorderTabsInGroup: (groupId, fromIndex, toIndex) => {
    const { tabGroups } = get();
    const group = tabGroups.find((g) => g.id === groupId);
    if (!group) return;

    const newTabIds = [...group.tabIds];
    const [movedTabId] = newTabIds.splice(fromIndex, 1);
    newTabIds.splice(toIndex, 0, movedTabId);

    set({
      tabGroups: tabGroups.map((g) =>
        g.id === groupId ? { ...g, tabIds: newTabIds } : g,
      ),
    });
  },

  reorderTabGroups: (fromIndex, toIndex) => {
    const { tabGroups } = get();
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= tabGroups.length) return;
    if (toIndex < 0 || toIndex >= tabGroups.length) return;

    const newGroups = [...tabGroups];
    const [movedGroup] = newGroups.splice(fromIndex, 1);
    newGroups.splice(toIndex, 0, movedGroup);

    set({ tabGroups: newGroups });
  },

  setTabGroupingSettings: (settings) => {
    const { tabGroupingSettings } = get();
    set({
      tabGroupingSettings: { ...tabGroupingSettings, ...settings },
    });
  },
});
